import dynamic from 'next/dynamic';

import clsx from 'clsx';

import { ValidateReturnType } from '@/utils/hooks/utils/useValidateCaption';

// Types
import type {
    CaptionLayoutConfig,
    CaptionWrapperStyle,
    SocialProfile,
    ValidationResult
} from '@/components/composite/Caption/types';

import {
    CaptionRestrictionsType,
    CaptionType
} from '@/lib/captionsHashtagLists/captionRestrictions';

// Components
import { Caption } from '@/components/composite/Caption';
import { CaptionAvatar } from '@/components/composite/Caption/shared/CaptionAvatar';
// Shared Components and Hooks
import { useCaptionInput } from '@/components/composite/Caption/shared/useCaptionInput';
const EmojiPicker = dynamic(() => import('@/components/base/EmojiPicker'));

type ThreadsCaptionProps = {
    captionLayoutConfig?: CaptionLayoutConfig;
    valid?: ValidationResult;
    caption: CaptionType;
    updateCaption: (caption: string) => void;
    validateCaptionCallback?: (caption: ValidateReturnType) => void;
    initialRestrictions?: CaptionRestrictionsType[];
    captionWrapperStyle?: CaptionWrapperStyle;
    footerPortalContainer?: HTMLElement;
    inputProps?: Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'style'>;
    rows?: number;
    forcedSocialProfile?: SocialProfile | null;
    rootId?: string;
};

export const ThreadsCaption = ({
    captionLayoutConfig = { hasBorder: true, hasAvatar: true, label: null, aiButton: false },
    valid,
    caption,
    updateCaption,
    validateCaptionCallback,
    initialRestrictions = [],
    captionWrapperStyle = 'default',
    footerPortalContainer,
    inputProps,
    rows = 8,
    forcedSocialProfile = null,
    rootId
}: ThreadsCaptionProps) => {
    const {
        t,
        currentProfile,
        captionRestrictions,
        wrapperRef,
        isFocused,
        handleBlur,
        handleChange,
        handleFocus,
        handleEmojiSelect
    } = useCaptionInput({
        caption,
        updateCaption,
        validateCaptionCallback,
        initialRestrictions,
        forcedSocialProfile,
        rootId,
        inputProps
    });

    const captionRef = wrapperRef as React.MutableRefObject<HTMLDivElement>;

    return (
        <Caption captionWrapperStyle={captionWrapperStyle} className="space-y-4" ref={captionRef}>
            {captionLayoutConfig?.label && captionLayoutConfig.label}
            <Caption.Content className="items-start">
                {captionLayoutConfig?.hasAvatar && <CaptionAvatar profile={currentProfile} />}
                <div className="flex-1">
                    <Caption.TextArea
                        {...inputProps}
                        onBlur={handleBlur}
                        value={caption}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        rows={rows}
                        className="w-full bg-[#101010] text-[17px] leading-[22px] text-[#E7E9EA] placeholder-gray-500 focus:outline-none focus:ring-0"
                        placeholder={t(
                            'scheduler:scheduler_funnel.post_settings.caption.placeholder'
                        )}
                    />
                </div>
                {captionLayoutConfig?.hasBorder && (
                    <Caption.Border
                        className={clsx('rounded-xl', {
                            'ring-gray-800': !isFocused,
                            'ring-gray-700': isFocused && valid?.valid,
                            'ring-yellow-600': isFocused && valid?.valid && valid?.warning,
                            'ring-red-500': !valid?.valid
                        })}
                    />
                )}
            </Caption.Content>
            <Caption.Footer>
                <div className="flex items-center space-x-4">
                    <EmojiPicker
                        placement="bottom-end"
                        offset={[5, 10]}
                        selectEmoji={handleEmojiSelect}
                        className="text-xl"
                        portalContainer={footerPortalContainer}
                    />
                </div>
                {captionRestrictions?.length > 0 && (
                    <Caption.Restrictions
                        className="flex-1 text-right"
                        valid={valid}
                        content={
                            <>
                                {captionRestrictions.map(({ type, restriction }, id) => (
                                    <span key={type}>
                                        {t(`commons:caption_restrictions.${type}`, {
                                            [type]: restriction?.limit
                                        })}
                                        {id + 1 !== captionRestrictions.length ? ', ' : null}
                                    </span>
                                ))}
                            </>
                        }
                    />
                )}
            </Caption.Footer>
        </Caption>
    );
};

export default ThreadsCaption;
